import { Component, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiTabsComponent, QaiTabDirective } from '@qai/ui';
import { AssignmentsTabComponent } from './assignments-tab/assignments-tab.component';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { UsersTabComponent } from "./users-tab/users-tab.component";
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AccountFeatureService } from 'src/app/services/account-feature/account-feature.service';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { PersonalizedPracticeTabComponent } from "./personalized-practice-tab/personalized-practice-tab.component";
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { IPersonalizedPracticeModulesRequest } from 'src/app/interfaces/reportingV2/ipersonalized-practice-modules-request';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { IPersonalPracticeSim } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-simulation';

@Component({
  selector: 'app-dashboard-tabs',
  templateUrl: './dashboard-tabs.component.html',
  styleUrls: ['./dashboard-tabs.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    QaiTabsComponent,
    QaiTabDirective,
    AssignmentsTabComponent,
    TranslocoModule,
    UsersTabComponent,
    PersonalizedPracticeTabComponent,
  ],
})
export class DashboardTabsComponent {
  @Input() selectedAccount: { label: string; value: string } | null = null;
  @Input() selectedTeams: string[] = [];
  @Input() isTeamAvailable: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() isNetworkCallInprogress: boolean = false;
  activeTab = '';
  isTranslationLoaded: boolean = false;
  showUserTab: boolean = false;
  showPersonalizedPracticeTab: boolean = false;
  hideIndividualUserData: boolean = false;
  hasPersonalizedProducts: boolean = false;
  hasPersonalizedProductsInAnyTeam: boolean = false;
  personalProducts: IPersonalPracticeSim[] = [];

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private featureFlagsService: FeatureFlagsService,
    private accountFeatureService: AccountFeatureService,
    private amplitudeService: AmplitudeService,
    private reportingDashboardService: ReportingDashboardService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      console.log(
        'selectedAccount changed:',
        changes['selectedAccount'].currentValue
      );
      this.checkPersonalizedProductsInAnyTeam();
    }
    if (changes['selectedTeams']) {
      this.loadPersonalizedPracticeSimulations();
      console.log(
        'selectedTeams changed:',
        changes['selectedTeams'].currentValue
      );
    }
  }

  async ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.activeTab = this.translocoService.translate('ASSIGNMENT_LABEL', {}, 'reportingV2');
    });

    this.hideIndividualUserData = await this.accountFeatureService.isFeatureActive(AppConstants.ACCOUNT_FEATURE_HIDE_INDIVIDUAL_USER_DATA);

    this.showUserTab = !this.hideIndividualUserData &&
      await this.featureFlagsService.isFlagEnabled(AppConstants.USER_TAB);
    console.log(
      `Flag for USER_TAB: ${this.showUserTab}`
    );

    this.showPersonalizedPracticeTab =
      await this.featureFlagsService.isFlagEnabled(
        AppConstants.PERSONALISED_PRACTICE_UI
      );
    console.log(
      `Flag for PERSONALIZED_PRACTICE_TAB: ${this.showPersonalizedPracticeTab}`
    );

    this.checkPersonalizedProductsInAnyTeam();
  }

  private checkPersonalizedProductsInAnyTeam(): void {
    if (!this.selectedAccount?.value) {
      this.hasPersonalizedProductsInAnyTeam = false;
      return;
    }

    const request: IPersonalizedPracticeModulesRequest = {
      accountUuid: this.selectedAccount.value,
      teamUuids: [],
    };

    this.reportingDashboardService
      .getPersonalizedPracticeModules(request, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .subscribe({
        next: (simulations) => {
          this.hasPersonalizedProductsInAnyTeam = simulations.length > 0;
          this.loadPersonalizedPracticeSimulations();
        },
        error: (error) => {
          console.error(
            'Error checking personalized practice products:',
            error
          );
          this.hasPersonalizedProductsInAnyTeam = false;
        },
      });
  }

  loadPersonalizedPracticeSimulations(): void {
    if (!this.selectedAccount?.value) {
      console.warn('No account selected');
      this.personalProducts = [];
      this.hasPersonalizedProducts = false;
      return;
    }

    const teamUuids = this.selectedTeams.includes('all')
      ? []
      : this.selectedTeams;

    const request: IPersonalizedPracticeModulesRequest = {
      accountUuid: this.selectedAccount.value,
      teamUuids: teamUuids,
    };

    this.reportingDashboardService
      .getPersonalizedPracticeModules(request, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .subscribe({
        next: (simulations) => {
          this.personalProducts = simulations;
          this.hasPersonalizedProducts = simulations.length > 0;
        },
        error: (error) => {
          console.error('Error loading personalized practice modules:', error);
          this.personalProducts = [];
          this.hasPersonalizedProducts = false;
        },
      });
  }

  onTabChange(event: any): void {
    // Accept both string and event object
    const tabId =
      typeof event === 'string'
        ? event
        : event?.detail?.tabId || event?.tabId || event?.id || '';
    if (tabId) {
      this.activeTab = tabId;
      console.log('Active tab changed to:', tabId);

      if (tabId === 'personalized-practice') {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.REPORTINGDASHBOARD_PERSONALIZED_PRACTICE_PAGEVIEW, {
            account_name: this.selectedAccount?.label || '',
          }
        );
      }
    }
  }
}
